import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import DataTable from './DataTable';
import aulaMensajesService from '../../services/AulaMensajesService';
import './AulaMensajesPanel.css';

const DEFAULT_ITEMS_PER_PAGE = 10;

const getRange = (page, pageSize) => {
  const take = Math.max(Number(pageSize) || DEFAULT_ITEMS_PER_PAGE, 1);
  const start = Math.max(page - 1, 0) * take;
  return { start, end: start + take - 1 };
};

const stripHtml = (value) => String(value || '').replace(/<[^>]+>/g, '');

const AulaMensajesPanel = ({ aulaId, embedded = false, service = aulaMensajesService, showAlumnoFilter = true }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [mensajes, setMensajes] = useState([]);
  const [mensajesTotal, setMensajesTotal] = useState(0);
  const [mensajesPage, setMensajesPage] = useState(1);
  const [mensajesPageSize, setMensajesPageSize] = useState(DEFAULT_ITEMS_PER_PAGE);
  const [filtro, setFiltro] = useState('todos');
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [titulo, setTitulo] = useState('');
  const [contenido, setContenido] = useState('');
  const [colorTexto, setColorTexto] = useState('#111827');
  const [saving, setSaving] = useState(false);
  const [modalLectura, setModalLectura] = useState(null);
  const [modalEliminar, setModalEliminar] = useState(null);
  const [modalOcultar, setModalOcultar] = useState(null);
  const [modalEditar, setModalEditar] = useState(null);
  const [editContenido, setEditContenido] = useState('');
  const [editTitulo, setEditTitulo] = useState('');
  const [editSaving, setEditSaving] = useState(false);
  const [editColorTexto, setEditColorTexto] = useState('#111827');
  const [toggleSaving, setToggleSaving] = useState(false);
  const [deleteSaving, setDeleteSaving] = useState(false);
  const editorDiv = useRef(null);
  const editEditorRef = useRef(null);

  const loadMensajes = useCallback(async () => {
    if (!aulaId) return;

    setIsLoading(true);
    setError('');
    try {
      const { start, end } = getRange(mensajesPage, mensajesPageSize);
      const response = await service.getMensajes(aulaId, {
        start,
        end,
        search: searchTerm.trim(),
        scope: filtro,
        includeTotal: true
      });
      const items = Array.isArray(response?.items) ? response.items : Array.isArray(response) ? response : [];
      setMensajes(items);
      setMensajesTotal(response?.total ?? items.length);
    } catch (err) {
      setError(err.message || 'No pudimos cargar los mensajes.');
      setMensajes([]);
      setMensajesTotal(0);
    } finally {
      setIsLoading(false);
    }
  }, [aulaId, filtro, mensajesPage, mensajesPageSize, searchTerm, service]);

  useEffect(() => {
    loadMensajes();
  }, [loadMensajes]);

  useEffect(() => {
    setMensajesPage(1);
  }, [searchTerm, filtro]);

  useEffect(() => {
    const totalPages = Math.max(1, Math.ceil(mensajesTotal / Math.max(mensajesPageSize, 1)));
    if (mensajesPage > totalPages) {
      setMensajesPage(totalPages);
    }
  }, [mensajesPage, mensajesPageSize, mensajesTotal]);

  useEffect(() => {
    if (!modalEditar || !editEditorRef.current) return;

    const html = modalEditar.contenidoHtml || modalEditar.ContenidoHtml || '';
    editEditorRef.current.innerHTML = html;
    setEditContenido(html);
    setEditTitulo(modalEditar.titulo || modalEditar.Titulo || '');
    setEditColorTexto('#111827');
  }, [modalEditar]);

  const resetCreateState = () => {
    setTitulo('');
    setContenido('');
    setColorTexto('#111827');
    if (editorDiv.current) {
      editorDiv.current.innerHTML = '';
    }
  };

  const applyCommand = (ref, command, value) => {
    if (!ref?.current) return;
    ref.current.focus();
    document.execCommand(command, false, value);
  };

  const closeCreateModal = () => {
    setShowModal(false);
    resetCreateState();
  };

  const handleCrearMensaje = async () => {
    const html = contenido;
    if (!html || html === '<p><br></p>' || html.trim() === '') {
      setError('Debes escribir un mensaje.');
      return;
    }

    setSaving(true);
    setError('');
    try {
      await service.crearMensaje(aulaId, {
        Titulo: titulo || undefined,
        ContenidoHtml: html,
        Activo: true
      });
      await loadMensajes();
      closeCreateModal();
    } catch (err) {
      setError(err.message || 'No pudimos crear el mensaje.');
    } finally {
      setSaving(false);
    }
  };

  const handleActualizarMensaje = async () => {
    if (!modalEditar) return;

    const html = editContenido;
    if (!html || html === '<p><br></p>' || html.trim() === '') {
      setError('Debes escribir un mensaje.');
      return;
    }

    setEditSaving(true);
    setError('');
    try {
      await service.actualizarMensaje(aulaId, modalEditar.id || modalEditar.Id, {
        Titulo: editTitulo || undefined,
        ContenidoHtml: html,
        Activo: modalEditar.activo ?? modalEditar.Activo ?? true
      });
      await loadMensajes();
      setModalEditar(null);
    } catch (err) {
      setError(err.message || 'No pudimos actualizar el mensaje.');
    } finally {
      setEditSaving(false);
    }
  };

  const handleEliminarMensaje = async () => {
    if (!modalEliminar) return;

    setDeleteSaving(true);
    setError('');
    try {
      await service.eliminarMensaje(aulaId, modalEliminar.id || modalEliminar.Id);
      await loadMensajes();
      setModalEliminar(null);
    } catch (err) {
      setError(err.message || 'No pudimos eliminar el mensaje.');
    } finally {
      setDeleteSaving(false);
    }
  };

  const handleToggleActivo = async () => {
    if (!modalOcultar) return;

    setToggleSaving(true);
    setError('');
    const nuevoActivo = !(modalOcultar.activo ?? modalOcultar.Activo ?? true);

    try {
      await service.actualizarMensaje(aulaId, modalOcultar.id || modalOcultar.Id, {
        ContenidoHtml: modalOcultar.contenidoHtml || modalOcultar.ContenidoHtml,
        Activo: nuevoActivo
      });
      await loadMensajes();
      setModalOcultar(null);
    } catch (err) {
      setError(err.message || 'No pudimos actualizar el estado del mensaje.');
    } finally {
      setToggleSaving(false);
    }
  };

  const mensajesColumns = useMemo(() => ([
    {
      key: 'fecha',
      title: 'Fecha',
      render: (_, mensaje) => {
        const fecha = mensaje.fechaAlta || mensaje.FechaAlta;
        return fecha ? new Date(fecha).toLocaleString() : '—';
      }
    },
    {
      key: 'remitente',
      title: 'Remitente',
      render: (_, mensaje) => mensaje.nombreRemitente || mensaje.NombreRemitente || mensaje.autorNombre || mensaje.AutorNombre || (mensaje.idUsuarioAutor ? 'Profesor' : 'Alumno')
    },
    {
      key: 'titulo',
      title: 'Título',
      render: (_, mensaje) => mensaje.titulo || mensaje.Titulo || '—'
    },
    {
      key: 'mensaje',
      title: 'Mensaje',
      render: (_, mensaje) => {
        const plain = stripHtml(mensaje.contenidoHtml || mensaje.ContenidoHtml || '');
        return plain.slice(0, embedded ? 110 : 180) + (plain.length > (embedded ? 110 : 180) ? '…' : '');
      }
    },
    {
      key: 'respuestas',
      title: 'Respuestas',
      className: 'text-center width-sm',
      render: (_, mensaje) => {
        const respuestas = Array.isArray(mensaje.respuestas || mensaje.Respuestas) ? (mensaje.respuestas || mensaje.Respuestas).length : 0;
        return respuestas;
      }
    },
    {
      key: 'acciones',
      title: 'Acciones',
      className: 'text-center width-sm',
      sortable: false,
      render: (_, mensaje) => {
        const activo = mensaje.activo ?? mensaje.Activo;
        return (
          <div className="action-buttons">
            <button className="action-edit-btn" title="Leer" onClick={() => setModalLectura(mensaje)}>
              <i className="fas fa-eye" />
            </button>
            <button className="action-edit-btn" title="Editar" onClick={() => setModalEditar(mensaje)}>
              <i className="fas fa-edit" />
            </button>
            <button className="action-remove-btn" title="Eliminar" onClick={() => setModalEliminar(mensaje)}>
              <i className="fas fa-trash" />
            </button>
            <button
              className={activo ? 'action-toggle-btn danger' : 'action-toggle-btn success'}
              title={activo ? 'Ocultar' : 'Mostrar'}
              onClick={() => setModalOcultar(mensaje)}
            >
              <i className={activo ? 'fas fa-eye-slash' : 'fas fa-eye'} />
            </button>
          </div>
        );
      }
    }
  ]), [embedded]);

  const renderEditorToolbar = (ref, currentColor, setCurrentColor) => (
    <div className="aula-messages-editor-toolbar">
      <button type="button" onClick={() => applyCommand(ref, 'bold')}>B</button>
      <button type="button" onClick={() => applyCommand(ref, 'italic')}>I</button>
      <button type="button" onClick={() => applyCommand(ref, 'underline')}>U</button>
      <button type="button" onClick={() => applyCommand(ref, 'insertUnorderedList')}>• Lista</button>
      <button type="button" onClick={() => applyCommand(ref, 'insertOrderedList')}>1. Lista</button>
      <button type="button" onClick={() => applyCommand(ref, 'justifyLeft')}>⟸</button>
      <button type="button" onClick={() => applyCommand(ref, 'justifyCenter')}>≡</button>
      <button type="button" onClick={() => applyCommand(ref, 'justifyRight')}>⟹</button>
      <button type="button" onClick={() => document.execCommand('formatBlock', false, 'H3')}>H3</button>
      <button type="button" onClick={() => document.execCommand('formatBlock', false, 'H4')}>H4</button>
      <div className="aula-messages-color-picker">
        <label>Texto</label>
        <input
          type="color"
          value={currentColor}
          onChange={(event) => {
            setCurrentColor(event.target.value);
            applyCommand(ref, 'foreColor', event.target.value);
          }}
        />
      </div>
    </div>
  );

  return (
    <div className={`aula-messages-panel${embedded ? ' embedded' : ''}`}>
      <div className={`aula-messages-card${embedded ? ' embedded' : ''}`}>
        <div className="aula-messages-header">
          <div className="aula-messages-title">
            <i className="fas fa-comments" />
            <span>Mensajes del aula</span>
          </div>
          <div className="aula-messages-toolbar">
            <div className="aula-messages-filter">
              <label>Filtrar</label>
              <select value={filtro} onChange={(event) => setFiltro(event.target.value)}>
                <option value="todos">Todos</option>
                <option value="mios">Mis mensajes</option>
                {showAlumnoFilter && <option value="alumnos">Mensajes de alumnos</option>}
              </select>
            </div>
            <div className="aula-messages-search">
              <i className="fas fa-search" aria-hidden="true" />
              <input
                type="text"
                placeholder="Buscar mensajes..."
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
              />
            </div>
            <button className="btn btn-primary" type="button" onClick={() => setShowModal(true)}>
              <i className="fas fa-plus" />
              <span>Nuevo mensaje</span>
            </button>
          </div>
        </div>

        <div className="aula-messages-content">
          {!isLoading && error && (
            <div className="data-table-empty">
              <div className="empty-icon">
                <i className="fas fa-exclamation-triangle" />
              </div>
              <p>{error}</p>
            </div>
          )}

          {!error && (
            <DataTable
              data={mensajes}
              columns={mensajesColumns}
              loading={isLoading}
              loadingMessage="Cargando mensajes..."
              emptyMessage="No hay mensajes todavía."
              externalPagination
              externalTotalItems={mensajesTotal}
              externalTotalPages={Math.max(1, Math.ceil(mensajesTotal / mensajesPageSize))}
              externalCurrentPage={mensajesPage}
              onPageChange={setMensajesPage}
              onItemsPerPageChange={(nextPageSize) => {
                setMensajesPageSize(nextPageSize);
                setMensajesPage(1);
              }}
              defaultItemsPerPage={mensajesPageSize}
            />
          )}
        </div>
      </div>

      {showModal && (
        <div className="aula-inline-modal-overlay">
          <div className="aula-inline-modal-card" onClick={(event) => event.stopPropagation()}>
            <div className="aula-inline-modal-header">
              <h3>Nuevo mensaje</h3>
              <button type="button" className="modal-close-btn" onClick={closeCreateModal}>
                <i className="fas fa-times" />
              </button>
            </div>
            <div className="aula-inline-modal-body">
              <label>Título</label>
              <input className="form-control" type="text" value={titulo} onChange={(event) => setTitulo(event.target.value)} />
              <label>Mensaje</label>
              {renderEditorToolbar(editorDiv, colorTexto, setColorTexto)}
              <div
                className="aula-messages-editor"
                ref={editorDiv}
                contentEditable
                onInput={(event) => setContenido(event.currentTarget.innerHTML)}
                suppressContentEditableWarning
              />
            </div>
            <div className="aula-inline-modal-footer">
              <button className="btn btn-secondary" onClick={closeCreateModal} disabled={saving}>
                Cancelar
              </button>
              <button className="btn btn-primary" onClick={handleCrearMensaje} disabled={saving}>
                {saving ? 'Guardando...' : 'Guardar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {modalLectura && (
        <div className="aula-inline-modal-overlay">
          <div className="aula-inline-modal-card" onClick={(event) => event.stopPropagation()}>
            <div className="aula-inline-modal-header">
              <h3>Mensaje</h3>
              <button type="button" className="modal-close-btn" onClick={() => setModalLectura(null)}>
                <i className="fas fa-times" />
              </button>
            </div>
            <div className="aula-inline-modal-body">
              <p className="aula-messages-meta">
                Publicado: {modalLectura.fechaAlta || modalLectura.FechaAlta ? new Date(modalLectura.fechaAlta || modalLectura.FechaAlta).toLocaleString() : '—'}
              </p>
              <div className="aula-messages-body" dangerouslySetInnerHTML={{ __html: modalLectura.contenidoHtml || modalLectura.ContenidoHtml }} />
            </div>
            <div className="aula-inline-modal-footer">
              <button className="btn btn-secondary" onClick={() => setModalLectura(null)}>Cerrar</button>
            </div>
          </div>
        </div>
      )}

      {modalEliminar && (
        <div className="aula-inline-modal-overlay">
          <div className="aula-inline-modal-card narrow" onClick={(event) => event.stopPropagation()}>
            <div className="aula-inline-modal-header">
              <h3>Eliminar mensaje</h3>
              <button type="button" className="modal-close-btn" onClick={() => setModalEliminar(null)}>
                <i className="fas fa-times" />
              </button>
            </div>
            <div className="aula-inline-modal-body">
              <p>¿Seguro que querés eliminar este mensaje? Esta acción no se puede deshacer.</p>
            </div>
            <div className="aula-inline-modal-footer">
              <button className="btn btn-secondary" onClick={() => setModalEliminar(null)} disabled={deleteSaving}>
                Cancelar
              </button>
              <button className="btn btn-danger" onClick={handleEliminarMensaje} disabled={deleteSaving}>
                {deleteSaving ? 'Eliminando...' : 'Eliminar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {modalOcultar && (
        <div className="aula-inline-modal-overlay">
          <div className="aula-inline-modal-card narrow" onClick={(event) => event.stopPropagation()}>
            <div className="aula-inline-modal-header">
              <h3>{(modalOcultar.activo ?? modalOcultar.Activo) ? 'Ocultar' : 'Mostrar'} mensaje</h3>
              <button type="button" className="modal-close-btn" onClick={() => setModalOcultar(null)}>
                <i className="fas fa-times" />
              </button>
            </div>
            <div className="aula-inline-modal-body">
              <p>¿Confirmás {(modalOcultar.activo ?? modalOcultar.Activo) ? 'ocultar' : 'mostrar'} este mensaje?</p>
            </div>
            <div className="aula-inline-modal-footer">
              <button className="btn btn-secondary" onClick={() => setModalOcultar(null)} disabled={toggleSaving}>
                Cancelar
              </button>
              <button className="btn btn-primary" onClick={handleToggleActivo} disabled={toggleSaving}>
                {toggleSaving ? 'Guardando...' : 'Confirmar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {modalEditar && (
        <div className="aula-inline-modal-overlay">
          <div className="aula-inline-modal-card" onClick={(event) => event.stopPropagation()}>
            <div className="aula-inline-modal-header">
              <h3>Editar mensaje</h3>
              <button type="button" className="modal-close-btn" onClick={() => setModalEditar(null)}>
                <i className="fas fa-times" />
              </button>
            </div>
            <div className="aula-inline-modal-body">
              <label>Título</label>
              <input className="form-control" type="text" value={editTitulo} onChange={(event) => setEditTitulo(event.target.value)} />
              <label>Mensaje</label>
              {renderEditorToolbar(editEditorRef, editColorTexto, setEditColorTexto)}
              <div
                className="aula-messages-editor"
                contentEditable
                ref={editEditorRef}
                onInput={(event) => setEditContenido(event.currentTarget.innerHTML)}
                suppressContentEditableWarning
              />
            </div>
            <div className="aula-inline-modal-footer">
              <button className="btn btn-secondary" onClick={() => setModalEditar(null)} disabled={editSaving}>
                Cancelar
              </button>
              <button className="btn btn-primary" onClick={handleActualizarMensaje} disabled={editSaving}>
                {editSaving ? 'Guardando...' : 'Guardar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AulaMensajesPanel;
